'use client';

import React, { useState } from 'react';
import { Plus, Receipt, Users, List } from 'lucide-react';
import SalesSummaryCards from './SalesSummaryCards';
import SalesTable from './SalesTable';
import SaleInvoiceForm from './SaleInvoiceForm';
import { FARMS_LIST, PAYMENT_STATUS_STYLES } from './salesData';
import { useRole } from '@/lib/useRole';

const TABS = [
  { id: 'invoices', label: 'Invoices', icon: List },
  { id: 'customers', label: 'Customers', icon: Users },
];

export default function SalesContent() {
  const [activeTab, setActiveTab] = useState('invoices');
  const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [farmFilter, setFarmFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const { can, roleLabel, roleColors } = useRole();

  const canCreateInvoice = can('create_invoices');
  const canManageSales = can('manage_sales');

  function handleSave(data: Record<string, unknown>) {
    setToast(`Invoice created successfully for ${data.customerName ?? 'customer'}.`);
    setTimeout(() => setToast(null), 4000);
  }

  return (
    <div className="p-6">
      {toast && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-3 bg-gray-900 text-white text-sm px-4 py-3 rounded-xl shadow-xl max-w-sm">
          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
          {toast}
        </div>
      )}

      {/* Page Header */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            Record egg and poultry sales, generate invoices, and track payment status by farm and branch.
          </p>
          {!canCreateInvoice && (
            <span className={`inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full text-xs font-semibold ${roleColors.bg} ${roleColors.text}`}>
              {roleLabel} — View Only
            </span>
          )}
        </div>
        {canCreateInvoice && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-sm flex-shrink-0"
          >
            <Plus size={16} />
            New Invoice
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <SalesSummaryCards />

      {/* Overdue Alert */}
      <div className="mb-5 flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
        <Receipt size={16} className="flex-shrink-0 text-red-500" />
        <span><span className="font-semibold">1 overdue invoice</span> — Jinja Poultry Traders owes UGX 5,100,000 (due 08 Sep 2026). Follow up required.</span>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 p-1 rounded-xl w-fit">
        {TABS.map(tab => {
          const TabIcon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
                isActive ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <TabIcon size={15} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'invoices' && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search invoice # or customer…"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-56"
            />
            <select value={farmFilter} onChange={e => setFarmFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">All Farms</option>
              {FARMS_LIST.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">All Statuses</option>
              {Object.entries(PAYMENT_STATUS_STYLES).map(([v, s]) => (
                <option key={v} value={v}>{s.label}</option>
              ))}
            </select>
          </div>
          <SalesTable farmFilter={farmFilter} statusFilter={statusFilter} search={search} canManage={canManageSales} />
        </>
      )}

      {activeTab === 'customers' && <CustomersTab />}

      {showForm && <SaleInvoiceForm onClose={() => setShowForm(false)} onSave={handleSave} />}
    </div>
  );
}

import { MOCK_CUSTOMERS, formatUGX } from './salesData';

function CustomersTab() {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Customer</th>
            <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Type</th>
            <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Phone</th>
            <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Address</th>
            <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs">Total Purchases</th>
            <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs">Outstanding</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {MOCK_CUSTOMERS.map(c => (
            <tr key={c.id} className="hover:bg-gray-50 transition-colors">
              <td className="px-4 py-3">
                <p className="font-semibold text-gray-900 text-xs">{c.name}</p>
                <p className="text-gray-400 text-xs">{c.email}</p>
              </td>
              <td className="px-4 py-3">
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 capitalize">{c.type}</span>
              </td>
              <td className="px-4 py-3 text-xs text-gray-600">{c.phone}</td>
              <td className="px-4 py-3 text-xs text-gray-500">{c.address}</td>
              <td className="px-4 py-3 text-right text-xs font-semibold text-gray-900">{formatUGX(c.totalPurchases)}</td>
              <td className="px-4 py-3 text-right text-xs font-semibold">
                <span className={c.outstandingBalance > 0 ? 'text-red-600' : 'text-green-600'}>
                  {formatUGX(c.outstandingBalance)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
